/**
 * collov_* a2ui `metadata.extensions` recognition (session surface).
 *
 * Kept apart from the JSX renderers (collovExtensions.jsx) so the renderer file
 * exports only components — react-refresh forbids mixing a component export with
 * plain functions/constants in one module.
 *
 * a2ui 1.0 lets a component carry `metadata.extensions.<vendor>_<name>`; a
 * conformant renderer MUST ignore an UNKNOWN extension key. The FOUR shaped
 * extensions in v1 (payload shapes mirror backend/widget-native/a2ui/extensions.json,
 * the SSOT L1 owns):
 *   collov_shopping_list (host List)     · collov_stat_grid   (host Row/List)
 *   collov_before_after  (host Row)      · collov_table        (host Column)
 */

export const COLLOV_EXTENSION_KEYS = [
  'collov_shopping_list', 'collov_stat_grid', 'collov_before_after', 'collov_table',
];

/** True when a recognised extension actually has something to draw. */
function hasContent(key, p) {
  if (!p || typeof p !== 'object') return false;
  if (key === 'collov_shopping_list') return Array.isArray(p.items) && p.items.length > 0;
  if (key === 'collov_stat_grid') {
    return (Array.isArray(p.stats) && p.stats.length > 0) || (Array.isArray(p.items) && p.items.length > 0);
  }
  if (key === 'collov_before_after') return !!(p.before?.url || p.after?.url || p.beforeUrl || p.afterUrl);
  if (key === 'collov_table') {
    return (Array.isArray(p.rows) && p.rows.length > 0) || (Array.isArray(p.columns) && p.columns.length > 0);
  }
  return false;
}

/**
 * The first recognised `collov_*` extension on a node's resolved `metadata`, or
 * null. A present-but-unrecognised key (`collov_future`) OR a recognised key with
 * an empty payload returns null so the caller renders the plain core children —
 * this IS the "otherwise render the plain core children" branch of the contract.
 */
export function recognisedCollovExtension(metadata) {
  const ext = metadata && typeof metadata === 'object' ? metadata.extensions : null;
  if (!ext || typeof ext !== 'object') return null;
  for (const key of COLLOV_EXTENSION_KEYS) {
    if (ext[key] && typeof ext[key] === 'object' && hasContent(key, ext[key])) {
      return { key, payload: ext[key] };
    }
  }
  return null;
}
